/**
 * O que faz: hooks React Query da aba Agentes, do sino de avisos e do histórico de etapas do lead.
 * Onde usa: SecaoAgentes, SinoAvisosAgentes e HistoricoEtapasLead.
 * Conexões: servico.ts e organizacao/servico.ts (correção da sugestão personalizada); toasts em português.
 * Tabelas: indiretamente as do servico.ts, sempre filtradas pela clínica.
 * Data: 25/09/2026.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mensagemErroAgentes, type ConfiguracaoAgentes, type RotaMarco, type CategoriaClassificacao } from './contratos';
import * as servico from './servico';
import { corrigirOrganizacaoLead } from './organizacao/servico';

export const chavesAgentes = {
  configuracao: (clinicaId: string | null) => ['agentes', 'configuracao', clinicaId] as const,
  revisao: (clinicaId: string | null) => ['agentes', 'revisao', clinicaId] as const,
  avisos: (clinicaId: string | null) => ['agentes', 'avisos', clinicaId] as const,
  movimentos: (clinicaId: string | null, leadId: string | null) => ['agentes', 'movimentos', clinicaId, leadId] as const,
};

export function useConfiguracaoAgentes(clinicaId: string | null) {
  return useQuery({
    queryKey: chavesAgentes.configuracao(clinicaId),
    queryFn: () => servico.carregarConfiguracao(clinicaId as string),
    enabled: Boolean(clinicaId),
    staleTime: 60_000,
  });
}

export function useRevisaoAgentes(clinicaId: string | null, modoAdmin: boolean, habilitado = true) {
  return useQuery({
    queryKey: chavesAgentes.revisao(clinicaId),
    queryFn: () => servico.listarRevisao(clinicaId as string, modoAdmin),
    enabled: Boolean(clinicaId) && habilitado,
    staleTime: 30_000,
  });
}

// Sino: sem Realtime nem polling agressivo; atualiza ao focar a janela e a cada 2 minutos.
export function useAvisosAgentes(clinicaId: string | null, modoAdmin: boolean, habilitado = true) {
  return useQuery({
    queryKey: chavesAgentes.avisos(clinicaId),
    queryFn: () => servico.contarAvisos(clinicaId as string, modoAdmin),
    enabled: Boolean(clinicaId) && habilitado,
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: 1,
  });
}

export function useMovimentosLead(clinicaId: string | null, leadId: string | null) {
  return useQuery({
    queryKey: chavesAgentes.movimentos(clinicaId, leadId),
    queryFn: () => servico.listarMovimentos(clinicaId as string, leadId as string),
    enabled: Boolean(clinicaId && leadId),
    staleTime: 30_000,
  });
}

export function useAcoesAgentes(clinicaId: string | null, modoAdmin: boolean) {
  const queryClient = useQueryClient();
  // Depois de qualquer decisão, revisão, avisos e Pipeline mudam juntos.
  const atualizar = () => {
    void queryClient.invalidateQueries({ queryKey: ['agentes'] });
    void queryClient.invalidateQueries({ queryKey: ['leads'] });
  };
  const comClinica = () => {
    if (!clinicaId) throw new Error('Clínica ativa não encontrada.');
    return clinicaId;
  };
  const erro = (padrao: string) => (falha: unknown) => toast.error(mensagemErroAgentes(falha, padrao));

  return {
    salvarConfiguracao: useMutation({
      mutationFn: (configuracao: ConfiguracaoAgentes) => servico.salvarConfiguracao(comClinica(), configuracao, modoAdmin),
      onSuccess: () => { atualizar(); toast.success('Configuração dos agentes salva.'); },
      onError: erro('Não foi possível salvar a configuração dos agentes.'),
    }),
    liberarColunasMeta: useMutation({
      mutationFn: (liberar: boolean) => servico.definirLiberacaoColunasMeta(comClinica(), liberar, modoAdmin),
      onSuccess: (_resultado, liberar) => {
        atualizar();
        toast.success(liberar ? 'A IA pode mover sozinha para colunas de venda e com evento Meta.' : 'Colunas de venda e com evento Meta voltaram a ser só sugestão.');
      },
      onError: erro('Não foi possível mudar esta autorização.'),
    }),
    salvarRota: useMutation({
      mutationFn: (rota: RotaMarco) => servico.salvarRota(comClinica(), rota, modoAdmin),
      onSuccess: () => { atualizar(); toast.success('Coluna do marco atualizada.'); },
      onError: erro('Não foi possível salvar a coluna deste marco.'),
    }),
    removerRota: useMutation({
      mutationFn: (categoria: CategoriaClassificacao) => servico.removerRota(comClinica(), categoria, modoAdmin),
      onSuccess: () => { atualizar(); toast.success('Marco sem coluna: o agente só classifica, sem mover.'); },
      onError: erro('Não foi possível remover a coluna deste marco.'),
    }),
    aprovarRetorno: useMutation({
      mutationFn: ({ sugestaoId, agendarPara }: { sugestaoId: string; agendarPara: string | null }) => servico.aprovarRetorno(sugestaoId, agendarPara),
      onSuccess: () => { atualizar(); toast.success('Retorno agendado.'); },
      onError: erro('Não foi possível agendar este retorno.'),
    }),
    recusarRetorno: useMutation({
      mutationFn: (sugestaoId: string) => servico.recusarRetorno(sugestaoId),
      onSuccess: () => { atualizar(); toast.success('Sugestão de retorno recusada.'); },
      onError: erro('Não foi possível recusar esta sugestão.'),
    }),
    aceitarEtapa: useMutation({
      mutationFn: (leadId: string) => servico.aceitarEtapa(comClinica(), leadId, modoAdmin),
      onSuccess: () => { atualizar(); toast.success('Card movido para a etapa sugerida.'); },
      onError: erro('Não foi possível mover o card.'),
    }),
    dispensarEtapa: useMutation({
      mutationFn: (leadId: string) => servico.dispensarEtapa(comClinica(), leadId, modoAdmin),
      onSuccess: () => { atualizar(); toast.success('Sugestão dispensada.'); },
      onError: erro('Não foi possível dispensar esta sugestão.'),
    }),
    corrigirOrganizacao: useMutation({
      mutationFn: ({ leadId, etapaId, etapaAlteradaEm }: { leadId: string; etapaId: string; etapaAlteradaEm: string | null }) =>
        corrigirOrganizacaoLead(comClinica(), leadId, etapaId, etapaAlteradaEm),
      onSuccess: () => {
        atualizar();
        // Selos do Pipeline mostram a pausa do lead corrigido logo depois da correção.
        void queryClient.invalidateQueries({ queryKey: ['lead-classification-states'] });
        toast.success('Card corrigido. A organização automática deste contato fica pausada.');
      },
      // O componente de correção mostra o erro ao lado da escolha; o toast reforça para quem já fechou.
      onError: erro('Não foi possível corrigir este card. Atualize e tente de novo.'),
    }),
    desfazerMovimento: useMutation({
      mutationFn: (movimentoId: string) => servico.desfazerMovimento(movimentoId),
      onSuccess: () => { atualizar(); toast.success('Movimento desfeito. A IA deste lead fica pausada até você retomar.'); },
      onError: erro('Não foi possível desfazer este movimento.'),
    }),
    analisarAgora: useMutation({
      mutationFn: () => servico.analisarAgora(comClinica()),
      onSuccess: (resultado) => {
        atualizar();
        toast.success(`Análise concluída: ${resultado.processados} leads, ${resultado.movidos} movidos, ${resultado.retornos} retornos sugeridos.`);
      },
      onError: (falha: unknown) => toast.error(falha instanceof Error ? falha.message : 'Não foi possível executar a análise agora.'),
    }),
  };
}
